import React, { useState, useEffect, useCallback } from "react";
import {
  BookOpen,
  Save,
  RotateCcw,
  Check,
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { AUTH_URL } from "../../constants";
import { useAuth } from "../../contexts/AuthContext";

// Fixed UUIDs match migration 077 seed — immutable by design.
const GLOBAL_TOPICS = [
  {
    id: "a1b2c3d4-0001-0001-0001-000000000001",
    slug: "border_security_immigration",
    canonical_name: "Border Security / Immigration",
  },
  {
    id: "a1b2c3d4-0002-0002-0002-000000000002",
    slug: "education_school_choice",
    canonical_name: "Education / School Choice",
  },
  {
    id: "a1b2c3d4-0003-0003-0003-000000000003",
    slug: "tax_reform_economic_policy",
    canonical_name: "Tax Reform / Economic Policy",
  },
  {
    id: "a1b2c3d4-0004-0004-0004-000000000004",
    slug: "water_rights_natural_resources",
    canonical_name: "Water Rights / Natural Resources",
  },
  {
    id: "a1b2c3d4-0005-0005-0005-000000000005",
    slug: "crime_safety_law_enforcement",
    canonical_name: "Crime & Safety / Law Enforcement",
  },
  {
    id: "a1b2c3d4-0006-0006-0006-000000000006",
    slug: "energy_policy_grid_reliability",
    canonical_name: "Energy Policy / Grid Reliability",
  },
  {
    id: "a1b2c3d4-0007-0007-0007-000000000007",
    slug: "election_integrity",
    canonical_name: "Election Integrity",
  },
  {
    id: "a1b2c3d4-0008-0008-0008-000000000008",
    slug: "healthcare_mental_health",
    canonical_name: "Healthcare / Mental Health",
  },
  {
    id: "a1b2c3d4-0009-0009-0009-000000000009",
    slug: "housing_property_rights",
    canonical_name: "Housing / Property Rights",
  },
  {
    id: "a1b2c3d4-0010-0010-0010-000000000010",
    slug: "federal_vs_state_authority",
    canonical_name: "Federal vs State Authority",
  },
  {
    id: "a1b2c3d4-0011-0011-0011-000000000011",
    slug: "veterans_affairs",
    canonical_name: "Veterans Affairs",
  },
  {
    id: "a1b2c3d4-0012-0012-0012-000000000012",
    slug: "family_social_issues",
    canonical_name: "Family / Social Issues",
  },
];

const emptyOverride = () => ({
  surface_label: "",
  enabled: true,
  priority: null,
});

export default function TopicTaxonomy() {
  const { token } = useAuth();
  const [federations, setFederations] = useState([]);
  const [selectedFederation, setSelectedFederation] = useState("");
  const [overrides, setOverrides] = useState({});
  const [savedOverrides, setSavedOverrides] = useState({});
  const [loadingFederations, setLoadingFederations] = useState(true);
  const [loadingOverrides, setLoadingOverrides] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const dirty = JSON.stringify(overrides) !== JSON.stringify(savedOverrides);

  const initOverrides = (apiRows = []) => {
    const map = {};
    GLOBAL_TOPICS.forEach((t) => {
      map[t.id] = emptyOverride();
    });
    apiRows.forEach((o) => {
      if (map[o.global_topic_id]) {
        map[o.global_topic_id] = {
          surface_label: o.surface_label || "",
          enabled: o.enabled !== false,
          priority: o.priority ?? null,
        };
      }
    });
    return map;
  };

  const loadFederations = useCallback(async () => {
    setLoadingFederations(true);
    setError(null);
    try {
      const r = await fetch(`${AUTH_URL}/admin/federations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const list = await r.json();
      setFederations(list);
      if (list.length > 0) setSelectedFederation(list[0].id);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingFederations(false);
    }
  }, [token]); // eslint-disable-line

  const loadOverrides = useCallback(
    async (federationId) => {
      if (!federationId) return;
      setLoadingOverrides(true);
      setError(null);
      try {
        const r = await fetch(
          `${AUTH_URL}/admin/federations/${federationId}/topic-overrides`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        const map = initOverrides(Array.isArray(data) ? data : []);
        setOverrides(map);
        setSavedOverrides(structuredClone(map));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoadingOverrides(false);
      }
    },
    [token],
  ); // eslint-disable-line

  useEffect(() => {
    loadFederations();
  }, [loadFederations]);

  useEffect(() => {
    if (selectedFederation) loadOverrides(selectedFederation);
  }, [selectedFederation, loadOverrides]);

  const setOverride = (topicId, field, value) => {
    setOverrides((prev) => ({
      ...prev,
      [topicId]: { ...prev[topicId], [field]: value },
    }));
  };

  const adjustPriority = (topicId, currentPriority, index, delta) => {
    const next = Math.max(
      1,
      Math.min(GLOBAL_TOPICS.length, (currentPriority ?? index + 1) + delta),
    );
    setOverride(topicId, "priority", next);
  };

  const reset = () => setOverrides(structuredClone(savedOverrides));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = GLOBAL_TOPICS.map((t) => ({
        global_topic_id: t.id,
        ...overrides[t.id],
      }));
      const r = await fetch(
        `${AUTH_URL}/admin/federations/${selectedFederation}/topic-overrides`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setSavedOverrides(structuredClone(overrides));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loadingFederations) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-5 h-5 text-slate-400 animate-spin mr-2" />
        <span className="text-slate-400">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-violet-50 rounded-lg flex-shrink-0">
            <BookOpen className="w-6 h-6 text-violet-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">
              Topic Taxonomy
            </h2>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">
              The global taxonomy is Hyve-maintained (12 V1 topics). Each
              federation can customize surface labels, reorder, or hide topics —
              but cannot add or remove global topics. Slugs and IDs are
              immutable to preserve cross-LD aggregation integrity.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-center gap-2 text-sm text-rose-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Federation selector + actions */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-slate-600 whitespace-nowrap">
            Federation:
          </label>
          {federations.length === 0 ? (
            <span className="text-sm text-slate-400">
              No federations yet — create one in Federation Settings.
            </span>
          ) : (
            <select
              value={selectedFederation}
              onChange={(e) => setSelectedFederation(e.target.value)}
              className="text-sm border border-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              {federations.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          )}
          {loadingOverrides && (
            <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={reset}
            disabled={!dirty}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 disabled:opacity-40 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving || !selectedFederation}
            className="flex items-center gap-1.5 text-sm px-4 py-1.5 text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-40 transition-colors"
          >
            {saved ? (
              <Check className="w-3.5 h-3.5" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            {saving ? "Saving..." : saved ? "Saved!" : "Save Overrides"}
          </button>
        </div>
      </div>

      {/* Topic table */}
      {selectedFederation && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide w-10">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Global Topic (immutable)
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Surface Label Override
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide w-20">
                  Visible
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide w-24">
                  Reorder
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {GLOBAL_TOPICS.map((topic, i) => {
                const ov = overrides[topic.id] || emptyOverride();
                const displayPriority = ov.priority ?? i + 1;
                return (
                  <tr
                    key={topic.id}
                    className={ov.enabled ? "" : "opacity-50 bg-slate-50/60"}
                  >
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs text-center">
                      {displayPriority}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">
                        {topic.canonical_name}
                      </div>
                      <div className="text-xs text-slate-400 font-mono mt-0.5">
                        {topic.slug}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={ov.surface_label}
                        onChange={(e) =>
                          setOverride(topic.id, "surface_label", e.target.value)
                        }
                        placeholder={topic.canonical_name}
                        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() =>
                          setOverride(topic.id, "enabled", !ov.enabled)
                        }
                        title={
                          ov.enabled ? "Hide this topic" : "Show this topic"
                        }
                        className={`p-1.5 rounded-lg transition-colors ${
                          ov.enabled
                            ? "text-emerald-600 bg-emerald-50 hover:bg-emerald-100"
                            : "text-slate-400 bg-slate-100 hover:bg-slate-200"
                        }`}
                      >
                        {ov.enabled ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() =>
                            adjustPriority(topic.id, ov.priority, i, -1)
                          }
                          disabled={displayPriority <= 1}
                          className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30 transition-colors"
                        >
                          <ChevronUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() =>
                            adjustPriority(topic.id, ov.priority, i, 1)
                          }
                          disabled={displayPriority >= GLOBAL_TOPICS.length}
                          className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30 transition-colors"
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Taxonomy integrity notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-700 leading-relaxed">
        <span className="font-medium">Taxonomy integrity:</span> Topic slugs
        (e.g.,{" "}
        <code className="font-mono bg-blue-100 px-1 rounded">
          border_security_immigration
        </code>
        ) are stored in{" "}
        <code className="font-mono bg-blue-100 px-1 rounded">
          topics_engaged
        </code>
        ,{" "}
        <code className="font-mono bg-blue-100 px-1 rounded">
          classified_topics
        </code>
        , and{" "}
        <code className="font-mono bg-blue-100 px-1 rounded">
          interest_topics
        </code>{" "}
        across all interaction types. Adding new global topics or changing slugs
        requires a taxonomy version bump and must be coordinated across all LDs
        — contact Hyve before modifying.
      </div>
    </div>
  );
}
